import { Users, Droplets, Weight, Clock, Ruler, ArrowUp, Check, X, Plus, ChevronLeft, Star, Shield, Sparkles } from "lucide-react";
import { ShopLinks } from "@/components/ShopLinks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Container } from "@/components/ui/container";
import { ImageGallery } from "@/components/ImageGallery";
import { cn } from "@/lib/utils";
import type { Zelt, ZeltTag } from "@/data/zelte";
import { kategorien } from "@/data/zelte";

interface ZeltDetailProps {
  zelt: Zelt;
  onNavigate: (path: string) => void;
  onToggleVergleich?: (zelt: Zelt) => void;
  isInVergleich?: boolean;
}

const tagLabels: Record<ZeltTag, string> = {
  neu: "Neu",
  bestseller: "Bestseller",
  sale: "Sale",
  empfohlen: "Empfohlen",
  leicht: "Ultraleicht",
  premium: "Premium",
};

export function ZeltDetail({ zelt, onNavigate, onToggleVergleich, isInVergleich = false }: ZeltDetailProps) {
  const hasDiscount = zelt.originalPreis && zelt.originalPreis > zelt.preis;
  const discountPercent = hasDiscount ? Math.round((1 - zelt.preis / zelt.originalPreis!) * 100) : 0;
  const kategorieName = kategorien.find(k => k.id === zelt.kategorie)?.name || zelt.kategorie;

  return (
    <article className="min-h-screen">
      {/* Hero Section with gradient background */}
      <div className="relative bg-gradient-to-b from-muted/50 to-background">
        <div className="absolute inset-0 mesh-gradient opacity-30" />

        <Container className="relative">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: "Zelte", href: "/zelte" },
              { label: kategorieName, href: `/zelte?kategorie=${zelt.kategorie}` },
              { label: zelt.name },
            ]}
            className="py-5"
          />

          {/* Back Button (Mobile) */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate("/zelte")}
            className="mb-4 -ml-2 md:hidden h-9 rounded-lg"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Zurück
          </Button>

          <div className="grid lg:grid-cols-2 gap-10 pb-16">
            {/* Image Section */}
            <div className="relative">
              {/* Tags overlay */}
              {zelt.tags.length > 0 && (
                <div className="absolute top-4 left-4 flex flex-wrap gap-2 z-20 pointer-events-none">
                  {zelt.tags.map(tag => (
                    <Badge
                      key={tag}
                      variant={tag as any}
                      className="text-sm px-3 py-1.5 shadow-lg backdrop-blur-md"
                    >
                      {tagLabels[tag]}
                    </Badge>
                  ))}
                </div>
              )}

              {hasDiscount && (
                <div className="absolute top-4 right-4 z-20 pointer-events-none">
                  <Badge variant="sale" className="text-base font-bold px-4 py-2 shadow-lg animate-pulse">
                    -{discountPercent}%
                  </Badge>
                </div>
              )}

              <ImageGallery images={zelt.bilder} alt={zelt.name} />
            </div>

            {/* Info Section */}
            <div className="space-y-6">
              {/* Brand */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-semibold text-primary uppercase tracking-wider">
                  {zelt.marke}
                </span>
                <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted/50 text-xs text-muted-foreground">
                  <Shield className="w-3 h-3" />
                  Verifizierter Händler
                </div>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">{zelt.name}</h1>

              {/* Rating */}
              <div className="flex items-center gap-4" role="img" aria-label={`Bewertung: ${zelt.bewertung} von 5 Sternen, basierend auf ${zelt.bewertungAnzahl} Bewertungen`}>
                <div className="flex items-center gap-1.5" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={cn(
                        "w-5 h-5",
                        i < Math.floor(zelt.bewertung)
                          ? "fill-amber-400 text-amber-400"
                          : i < zelt.bewertung
                          ? "fill-amber-400/50 text-amber-400"
                          : "fill-muted text-muted"
                      )}
                    />
                  ))}
                </div>
                <span className="text-lg font-semibold">{zelt.bewertung}</span>
                <span className="text-muted-foreground">
                  ({zelt.bewertungAnzahl} Bewertungen)
                </span>
              </div>

              {/* Short description */}
              <p className="text-lg text-muted-foreground leading-relaxed">{zelt.kurzbeschreibung}</p>

              {/* Price */}
              <div className="flex items-baseline gap-4 py-4 border-y border-border/50">
                <span className="text-4xl md:text-5xl font-bold tracking-tight">{zelt.preis}€</span>
                {hasDiscount && (
                  <>
                    <span className="text-xl text-muted-foreground line-through">{zelt.originalPreis}€</span>
                    <span className="text-sm font-semibold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-lg">
                      Sie sparen {zelt.originalPreis! - zelt.preis}€
                    </span>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <ShopLinks zelt={zelt} size="lg" className="flex-1" />
                {onToggleVergleich && (
                  <Button
                    size="lg"
                    variant={isInVergleich ? "default" : "outline"}
                    onClick={() => onToggleVergleich(zelt)}
                    className={cn(
                      "h-14 px-6 rounded-xl transition-all duration-300",
                      isInVergleich && "bg-primary"
                    )}
                  >
                    {isInVergleich ? (
                      <>
                        <Check className="h-5 w-5 mr-2" />
                        Im Vergleich
                      </>
                    ) : (
                      <>
                        <Plus className="h-5 w-5 mr-2" />
                        Vergleichen
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Quick Specs */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pt-4">
                {[
                  { icon: Users, label: "Personen", value: zelt.personen.toString(), color: "from-blue-500/20 to-indigo-500/20" },
                  { icon: Droplets, label: "Wassersäule", value: `${zelt.wassersaeule}mm`, color: "from-cyan-500/20 to-sky-500/20" },
                  { icon: Weight, label: "Gewicht", value: `${zelt.gewicht}kg`, color: "from-amber-500/20 to-orange-500/20" },
                  { icon: Clock, label: "Aufbau", value: `${zelt.aufbauzeit} Min`, color: "from-green-500/20 to-emerald-500/20" },
                  { icon: Ruler, label: "Packmaß", value: zelt.packmass, color: "from-purple-500/20 to-violet-500/20" },
                  { icon: ArrowUp, label: "Höhe", value: `${zelt.hoehe}cm`, color: "from-rose-500/20 to-pink-500/20" },
                ].map(({ icon: Icon, label, value, color }) => (
                  <div
                    key={label}
                    className={cn(
                      "flex items-center gap-3 p-4 rounded-xl",
                      "bg-gradient-to-br transition-all duration-300 hover:scale-[1.02]",
                      color
                    )}
                  >
                    <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-[11px] text-muted-foreground uppercase tracking-wide truncate">{label}</div>
                      <div className="font-bold truncate">{value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Category Badge */}
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 text-sm font-medium">
                  <Sparkles className="w-4 h-4 text-primary" />
                  {zelt.saison}-Saison Zelt
                </div>
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-muted/50 text-sm text-muted-foreground">
                  {kategorieName}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      {/* Details Section */}
      <Container>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 py-12">
          {/* Beschreibung */}
          <Card className="lg:col-span-2 border-0 shadow-lg bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                Beschreibung
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">{zelt.beschreibung}</p>
            </CardContent>
          </Card>

          {/* Features */}
          <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-blue-500" />
                </div>
                Features
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {zelt.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="w-5 h-5 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-blue-500" />
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Vorteile */}
          <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-500" />
                </div>
                <span className="text-green-600 dark:text-green-400">Vorteile</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {zelt.vorteile.map((vorteil, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="w-5 h-5 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="h-3 w-3 text-green-500" />
                    </span>
                    <span>{vorteil}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Nachteile */}
          <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <X className="w-4 h-4 text-red-500" />
                </div>
                <span className="text-red-600 dark:text-red-400">Nachteile</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {zelt.nachteile.map((nachteil, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm">
                    <span className="w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <X className="h-3 w-3 text-red-500" />
                    </span>
                    <span>{nachteil}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Technische Daten */}
          <Card className="border-0 shadow-lg bg-card/50 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Ruler className="w-4 h-4 text-primary" />
                </div>
                Technische Daten
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3 text-sm">
                {[
                  { label: "Innenraum", value: zelt.innenraum },
                  { label: "Stehhöhe", value: `${zelt.hoehe}cm` },
                  { label: "Gewicht", value: `${zelt.gewicht}kg` },
                  { label: "Packmaß", value: zelt.packmass },
                  { label: "Wassersäule", value: `${zelt.wassersaeule}mm` },
                  { label: "Aufbauzeit", value: `~${zelt.aufbauzeit} Min` },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center py-2 border-b border-border/30 last:border-0">
                    <dt className="text-muted-foreground">{label}</dt>
                    <dd className="font-semibold text-right">{value}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        </div>
      </Container>
    </article>
  );
}
